import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { toast } from 'sonner';
import { setUser as setSentryUser } from '@/lib/sentry';
import { ROLES, isStaffRole, can as canRole, canAccessRoute as canRoleAccessRoute } from '@/lib/permissions';

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isCoach, setIsCoach] = useState(false);
  const [loading, setLoading] = useState(true);
  // MFA state
  const [aalLevel, setAalLevel] = useState(null);   // 'aal1' | 'aal2' | null
  const [mfaFactors, setMfaFactors] = useState([]); // enrolled TOTP factors

  /** Refresh the AAL level and enrolled factors from Supabase Auth. */
  const refreshMfaState = useCallback(async () => {
    try {
      const [{ data: aalData }, { data: factorsData }] = await Promise.all([
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        supabase.auth.mfa.listFactors(),
      ]);
      setAalLevel(aalData?.currentLevel ?? null);
      setMfaFactors(factorsData?.totp ?? []);
    } catch (err) {
      console.error('Error refreshing MFA state:', err);
    }
  }, []);

  const checkCoachStatus = useCallback(async (currentUser) => {
    if (!currentUser) {
      setIsCoach(false);
      setProfile(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', currentUser.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setProfile(data);
        setIsCoach(isStaffRole(data.role));
      } else {
        setIsCoach(false);
        setProfile(null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setIsCoach(false);
      setProfile(null);
      toast.error("Profile Error", { description: "Could not fetch user profile." });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let initialised = false;

    const applySession = async (session, { blockOnProfile }) => {
      if (cancelled) return;
      setSession(session);
      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (currentUser) {
        setSentryUser({ id: currentUser.id });
        if (blockOnProfile) {
          await checkCoachStatus(currentUser);
          await refreshMfaState();
        } else {
          checkCoachStatus(currentUser);
          refreshMfaState();
        }
      } else {
        setSentryUser(null);
        setIsCoach(false);
        setProfile(null);
        setAalLevel(null);
        setMfaFactors([]);
      }
    };

    const finishInitialisation = () => {
      if (initialised || cancelled) return;
      initialised = true;
      setLoading(false);
    };

    // Explicitly resolve the initial session so the UI never hangs waiting
    // for an INITIAL_SESSION event from onAuthStateChange, which isn't
    // guaranteed to fire before subscribe() returns on cold loads.
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        await applySession(session, { blockOnProfile: true });
      } catch (error) {
        console.error('Error resolving initial session:', error);
      } finally {
        finishInitialisation();
      }
    })();

    // Safety net: never let the app hang on the loading spinner.
    const failsafe = setTimeout(finishInitialisation, 5000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'INITIAL_SESSION') {
          // Handled by getSession() above — just ensure the flag is cleared.
          finishInitialisation();
          return;
        }
        applySession(session, { blockOnProfile: false }).catch((error) => {
          console.error('Error handling auth state change:', error);
        });
      }
    );

    return () => {
      cancelled = true;
      clearTimeout(failsafe);
      subscription.unsubscribe();
    };
  }, [checkCoachStatus]);

  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error("Sign in Failed", { description: error.message || "Something went wrong" });
    }
    
    return { data, error };
  }, []);

  /**
   * Enroll a new TOTP factor. Returns { qrCode, secret, factorId } on success.
   * The admin must then call verifyEnrollment with the first OTP code.
   */
  const enrollTotp = useCallback(async (friendlyName = 'Authenticator') => {
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName,
    });
    if (error) return { error };
    return {
      factorId: data.id,
      qrCode: data.totp.qr_code,
      secret: data.totp.secret,
      error: null,
    };
  }, []);

  /**
   * Verify the first code after enrollment to activate the factor.
   */
  const verifyEnrollment = useCallback(async (factorId, code) => {
    const { data: challengeData, error: challengeErr } =
      await supabase.auth.mfa.challenge({ factorId });
    if (challengeErr) return { error: challengeErr };

    const { data, error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.id,
      code,
    });
    if (!error) await refreshMfaState();
    return { data, error };
  }, [refreshMfaState]);

  /**
   * Complete a login MFA challenge (AAL1 → AAL2).
   * Call this after signIn when the admin has enrolled factors.
   */
  const challengeAndVerifyTotp = useCallback(async (factorId, code) => {
    const { data: challengeData, error: challengeErr } =
      await supabase.auth.mfa.challenge({ factorId });
    if (challengeErr) return { error: challengeErr };

    const { data, error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.id,
      code,
    });
    if (!error) {
      // Session is now AAL2; refresh the Supabase client session so the
      // new AAL2 JWT is used for subsequent requests.
      await supabase.auth.refreshSession();
      await refreshMfaState();
    }
    return { data, error };
  }, [refreshMfaState]);

  /**
   * Register a new one-use MFA token in admin_deletion_mfa_tokens for use
   * in the deletion workflow. Returns the token id.
   * Call this immediately before initiating a deletion execute.
   */
  const createDeletionMfaToken = useCallback(async (factorId, code) => {
    // 1. Fresh TOTP challenge in the browser (updates JWT to AAL2 with new amr).
    const { data: challengeData, error: challengeErr } =
      await supabase.auth.mfa.challenge({ factorId });
    if (challengeErr) return { error: challengeErr };

    const { error: verifyErr } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.id,
      code,
    });
    if (verifyErr) return { error: verifyErr };

    // 2. Refresh so the AAL2 JWT is current for the Edge Function call.
    const { data: refreshData, error: refreshErr } = await supabase.auth.refreshSession();
    if (refreshErr || !refreshData?.session?.access_token) {
      return { error: refreshErr ?? new Error('Failed to refresh session after TOTP') };
    }

    // 3. Ask the Edge Function (service_role) to mint the one-use ticket.
    //    The browser cannot insert into admin_deletion_mfa_tokens (RLS).
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-delete-user-account-v2`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${refreshData.session.access_token}`,
        },
        body: JSON.stringify({ op: 'mint_mfa_token' }),
      },
    );
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { error: new Error(json.error ?? `HTTP ${res.status}`) };
    }
    return { tokenId: json.token_id, error: null };
  }, []);

  /** Unenroll (remove) a TOTP factor by its id. */
  const unenrollFactor = useCallback(async (factorId) => {
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (!error) await refreshMfaState();
    return { error };
  }, [refreshMfaState]);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) {
      const storageKey = `sb-zfaucugicbitpmapnakj-auth-token`;
      localStorage.removeItem(storageKey);
      setUser(null);
      setSession(null);
      setIsCoach(false);
      setProfile(null);
      setSentryUser(null);
    }
    return { error: null };
  }, []);

  const role = isCoach ? profile?.role ?? null : null;

  const value = useMemo(() => ({
    user,
    session,
    profile,
    // isCoach means "staff" (admin or support) and gates the whole app.
    isCoach,
    role,
    isAdmin: role === ROLES.ADMIN,
    isSupport: role === ROLES.SUPPORT,
    can: (feature) => canRole(role, feature),
    canAccessRoute: (pathname) => canRoleAccessRoute(role, pathname),
    loading,
    // MFA
    aalLevel,
    mfaFactors,
    isAal2: aalLevel === 'aal2',
    hasTotpFactor: mfaFactors.length > 0,
    // Actions
    signIn,
    signOut,
    enrollTotp,
    verifyEnrollment,
    challengeAndVerifyTotp,
    createDeletionMfaToken,
    unenrollFactor,
    refreshMfaState,
  }), [user, session, profile, isCoach, role, loading,
       aalLevel, mfaFactors,
       signIn, signOut,
       enrollTotp, verifyEnrollment, challengeAndVerifyTotp,
       createDeletionMfaToken, unenrollFactor, refreshMfaState]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};